import { TaskStatusSchema, UuidSchema, type TaskStatus } from "@mind/contracts/browser";

export type WorkView = "board" | "list";
export type WorkAssignee = "me";
export type WorkViewport = "wide" | "compact";

export function effectiveWorkView(search: Pick<WorkSearch, "view">, viewport: WorkViewport): WorkView {
  return search.view ?? (viewport === "compact" ? "list" : "board");
}
export type WorkSearch = {
  readonly workspace?: string;
  readonly view?: WorkView;
  readonly project?: string;
  readonly status?: TaskStatus;
  readonly assignee?: WorkAssignee;
  readonly filter?: string;
  readonly task?: string;
};

const WORK_SEARCH_KEYS: Readonly<Record<string, true>> = {
  workspace: true,
  view: true,
  project: true,
  status: true,
  assignee: true,
  filter: true,
  task: true,
};
const STATUS_VALUES: Record<TaskStatus, true> = {
  inbox: true,
  planned: true,
  in_progress: true,
  review: true,
  done: true,
};

export function validateWorkSearch(input: unknown): WorkSearch {
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    throw new TypeError("Work route search must be an object");
  }
  const search = input as Readonly<Record<string, unknown>>;
  const unexpectedKey = Object.keys(search).find((key) => WORK_SEARCH_KEYS[key] !== true);
  if (unexpectedKey !== undefined) throw new TypeError(`Unexpected Work route search key: ${unexpectedKey}`);

  const workspace = parseOptionalUuid(search["workspace"]);
  const project = parseOptionalUuid(search["project"]);
  const task = parseOptionalUuid(search["task"]);
  const view = parseOptionalEnum(search["view"], ["board", "list"] as const, "view");
  const status = parseOptionalStatus(search["status"]);
  const assignee = search["assignee"] === undefined ? undefined : search["assignee"] === "me" ? "me" : invalid("assignee");
  let filter: string | undefined;
  if (search["filter"] !== undefined) {
    if (typeof search["filter"] !== "string" || search["filter"].trim().length > 100) invalid("filter");
    filter = search["filter"].trim() || undefined;
  }

  return {
    ...(workspace === undefined ? {} : { workspace }),
    ...(view === undefined ? {} : { view }),
    ...(project === undefined ? {} : { project }),
    ...(status === undefined ? {} : { status }),
    ...(assignee === undefined ? {} : { assignee }),
    ...(filter === undefined ? {} : { filter }),
    ...(task === undefined ? {} : { task }),
  };
}

export function stripWorkSearch(input: Readonly<Record<string, unknown>>): WorkSearch {
  const allowed: Record<string, unknown> = {};
  for (const key of Object.keys(WORK_SEARCH_KEYS)) {
    if (input[key] !== undefined && input[key] !== null && input[key] !== "") allowed[key] = input[key];
  }
  return validateWorkSearch(allowed);
}

export function withWorkTask(current: WorkSearch, task: string | null): WorkSearch {
  return validateWorkSearch({ ...current, task: task ?? undefined });
}

export function withWorkProject(current: WorkSearch, project: string | null): WorkSearch {
  return validateWorkSearch({ ...current, project: project ?? undefined, task: undefined });
}

export function withWorkStatus(current: WorkSearch, status: WorkSearch["status"] | null): WorkSearch {
  return validateWorkSearch({ ...current, status: status ?? undefined });
}

export function withWorkView(current: WorkSearch, view: WorkView): WorkSearch {
  return validateWorkSearch({ ...current, view });
}

function parseOptionalUuid(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  const parsed = UuidSchema.safeParse(value);
  if (!parsed.success) return invalid("identifier");
  return parsed.data;
}

function parseOptionalStatus(value: unknown): WorkSearch["status"] {
  if (value === undefined) return undefined;
  const parsed = TaskStatusSchema.safeParse(value);
  if (!parsed.success || STATUS_VALUES[parsed.data] !== true) return invalid("status");
  return parsed.data;
}

function parseOptionalEnum<const T extends readonly string[]>(value: unknown, values: T, key: string): T[number] | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string" || !values.includes(value)) return invalid(key);
  return value;
}

function invalid(key: string): never {
  throw new TypeError(`Invalid Work ${key}`);
}


